import type { PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import {
  efrisSettingsSchema,
  getEfrisSettings,
  retryEfris,
  scheduleEfris,
} from './efris.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createEfrisRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/efris', authenticate({ db, config }));
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const need = (req: Request, p: string) => {
    if (!can(req, p))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };

  router.get(
    '/efris/settings',
    asyncHandler(async (req, res) => {
      need(req, 'settings.company.view');
      const company = await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } });
      res.json({
        ...(await getEfrisSettings(db, req.auth!.companyId)),
        vatRegistered: company.vatRegistered,
      });
    }),
  );
  router.put(
    '/efris/settings',
    asyncHandler(async (req, res) => {
      need(req, 'settings.company.edit');
      const body = parse(efrisSettingsSchema, req.body);
      const companyId = req.auth!.companyId;
      await db.setting.upsert({
        where: { companyId_key: { companyId, key: 'efris' } },
        create: { companyId, key: 'efris', value: body },
        update: { value: body },
      });
      await audit(
        db,
        { companyId, userId: req.auth!.userId, ip: clientIp(req) },
        { action: 'efris.settings_updated', entity: 'settings', after: body },
      );
      res.json(body);
    }),
  );
  router.get(
    '/efris/status/:docType/:id',
    asyncHandler(async (req, res) => {
      need(req, 'sales.invoice.view');
      const docType = String(req.params.docType);
      if (!['invoice', 'creditnote'].includes(docType))
        throw new HttpError(400, 'VALIDATION_ERROR', 'Unknown document type');
      const doc = await db.salesDocument.findFirst({
        where: {
          id: Number(req.params.id),
          companyId: req.auth!.companyId,
          docType,
          ...(req.auth!.scope === 'own' ? { ownerId: req.auth!.userId } : {}),
        },
      });
      if (!doc) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
      const s = await db.efrisSubmission.findUnique({
        where: { docType_docId: { docType, docId: doc.id } },
      });
      const settings = await getEfrisSettings(db, req.auth!.companyId);
      res.json(
        s
          ? {
              enabled: settings.enabled,
              status: s.status,
              attempts: s.attempts,
              fiscalNumber: s.fiscalNumber,
              verificationCode: s.verificationCode,
              qrData: s.qrData,
              error: s.error,
              submittedAt: s.submittedAt,
              sandbox: settings.mode === 'sandbox',
            }
          : { enabled: settings.enabled, status: 'none' },
      );
    }),
  );
  router.post(
    '/efris/retry/:docType/:id',
    asyncHandler(async (req, res) => {
      need(req, 'sales.invoice.approve');
      const docType = String(req.params.docType);
      const doc = await db.salesDocument.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId, docType },
      });
      if (!doc) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
      const existing = await db.efrisSubmission.findUnique({
        where: { docType_docId: { docType, docId: doc.id } },
      });
      if (!existing) await scheduleEfris(db, doc);
      else await retryEfris(db, docType, doc.id);
      await audit(
        db,
        { companyId: req.auth!.companyId, userId: req.auth!.userId, ip: clientIp(req) },
        { action: 'efris.retry', entity: 'sales_document', entityId: doc.id },
      );
      res.status(202).json({ ok: true });
    }),
  );
  return router;
}
